import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PasswordField, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Role } from '../../../models/role';
import { Route } from '../../../models/route';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions, FormSection, FullRow, apiError } from '../shared/AdminUi';
import SeatsNotice, { isSeatLimit } from './SeatsNotice';
import OfficeRoutesPicker, { OfficeRoutesSelection } from '../shared/OfficeRoutesPicker';

type Field = 'firstName' | 'lastName' | 'username' | 'roleId' | 'password' | 'passwordConfirm' | 'offices';

/** Crear o editar un usuario de la web: datos, acceso y sedes/rutas asignadas. */
const UserFormPage: React.FC<{ mode: 'new' | 'edit' }> = ({ mode }) => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canCreate, canUpdate } = useResourcePermissions('/admin/users');
  const allowed = mode === 'new' ? canCreate : canUpdate;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [roleId, setRoleId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [changePassword, setChangePassword] = useState(mode === 'new');
  const [offices, setOffices] = useState<Office[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<OfficeRoutesSelection[]>([]);
  const [routesByOffice, setRoutesByOffice] = useState<Record<string, Route[]>>({});
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);

  setPageTitle(mode === 'new' ? 'users.newTitle' : 'users.editTitle');
  useEffect(() => {
    (async () => {
      try {
        const [resRoles, resOffices, resRoutes, resUser] = await Promise.all([
          axios.get('roles'),
          axios.get('offices/all'),
          axios.get('routes/all'),
          ...(mode === 'edit' ? [axios.get(`users/${id}`)] : []),
        ]);
        setRoles(Array.isArray(resRoles.data) ? resRoles.data : []);
        setOffices(Array.isArray(resOffices.data?.data) ? resOffices.data.data : []);
        const grouped: Record<string, Route[]> = {};
        (Array.isArray(resRoutes.data?.data) ? resRoutes.data.data : []).forEach((route: Route) => {
          const officeId = route.office?.id?.toString();
          if (!officeId) return;
          if (!grouped[officeId]) grouped[officeId] = [];
          grouped[officeId].push(route);
        });
        setRoutesByOffice(grouped);
        if (resUser) {
          const user = resUser.data;
          setFirstName(user.firstName ?? '');
          setLastName(user.lastName ?? '');
          setUsername(user.username ?? '');
          setIdNumber(user.idNumber ?? '');
          setRoleId(user.role?.id != null ? String(user.role.id) : '');
          setOfficeRoutes(
            (Array.isArray(user.offices) ? user.offices : []).map((entry: any) => ({
              office_id: entry.officeId.toString(),
              selectedCollects: (entry.routeIds ?? []).map((rid: number) => rid.toString()),
            })),
          );
        }
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const validate = () => {
    const req = t('common.required');
    const e: Partial<Record<Field, string>> = {};
    if (!firstName.trim()) e.firstName = req;
    if (!lastName.trim()) e.lastName = req;
    if (!username.trim()) e.username = req;
    if (!roleId) e.roleId = req;
    if (changePassword) {
      if (!password) e.password = mode === 'new' ? req : t('users.passwordRequired');
      if (mode === 'new' && !passwordConfirm) e.passwordConfirm = req;
      else if (password !== passwordConfirm) e.passwordConfirm = t('users.passwordMismatch');
    }
    if (officeRoutes.length === 0 || officeRoutes.some((s) => !s.office_id || s.selectedCollects.length === 0)) {
      e.offices = t('users.officesRequired');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!allowed) return;
    if (!validate()) {
      notify.warning(t('users.missingFields'));
      return;
    }
    const officesBody = officeRoutes.map((item) => ({
      officeId: parseInt(item.office_id),
      routeIds: item.selectedCollects.map((rid) => parseInt(rid)),
    }));
    setSaving(true);
    try {
      if (mode === 'new') {
        await axios.post('users', {
          firstName,
          lastName,
          idNumber,
          username,
          password,
          passwordConfirm,
          roleId: Number(roleId),
          userTypeId: 2,
          offices: officesBody,
        });
        notify.success(t('users.createdOk'));
      } else {
        await axios.put(`users/${id}`, {
          firstName,
          lastName,
          idNumber,
          username,
          roleId: Number(roleId),
          password: changePassword ? password : undefined,
          offices: officesBody,
        });
        notify.success(t('users.updatedOk'));
      }
      navigate('/admin/users');
    } catch (error) {
      notify.error(isSeatLimit(error) ? t('users.seatLimitError') : apiError(error, mode === 'new' ? t('users.createError') : t('users.updateError')));
    } finally {
      setSaving(false);
    }
  };

  const err = (f: Field) => ({ error: !!errors[f], helperText: errors[f] });
  const title = mode === 'new' ? t('users.newTitle') : t('users.editTitle');

  return (
    <AdminPage narrow>
      <PageHeader
        title={title}
        description={t('users.formSubtitle')}
        breadcrumbs={[{ label: t('users.title'), href: '#/admin/users' }, { label: title }]}
        helpAction={<ScreenTourButton />}
      />
      {/* Un usuario nuevo queda activo: en las empresas prepagadas ocupa un cupo. */}
      {mode === 'new' && <SeatsNotice />}
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box data-tour="user-form-person">
          <FormSection title={t('users.sectionPerson')}>
            <TextField id="user-first-name" label={t('common.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required fullWidth {...err('firstName')} />
            <TextField id="user-last-name" label={t('common.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} required fullWidth {...err('lastName')} />
            <TextField id="user-id-number" label={t('common.idNumber')} value={idNumber} onChange={(e) => setIdNumber(e.target.value)} fullWidth />
          </FormSection>
        </Box>
        <Box data-tour="user-form-access">
          <FormSection title={t('users.sectionAccess')}>
            <TextField
              id="user-username"
              label={t('common.username')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              fullWidth
              autoComplete="off"
              {...err('username')}
            />
            <TextField id="user-role" data-tour="user-form-role" select label={t('common.role')} value={roles.length ? roleId : ''} onChange={(e) => setRoleId(e.target.value)} required fullWidth {...err('roleId')}>
              {roles.map((role: Role) => (
                <MenuItem key={role.id} value={String(role.id)}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>
            {mode === 'edit' && (
              <FullRow>
                <FormControlLabel
                  data-tour="user-form-change-password"
                  control={
                    <Switch
                      checked={changePassword}
                      onChange={() => {
                        setChangePassword(!changePassword);
                        // Al activar o desactivar el cambio, se empieza con los campos vacíos.
                        setPassword('');
                        setPasswordConfirm('');
                      }}
                    />
                  }
                  label={t('users.changePassword')}
                />
              </FullRow>
            )}
            {changePassword && (
              <>
                <PasswordField
                  id="user-password"
                  data-tour="user-form-password"
                  label={mode === 'new' ? t('common.password') : t('users.newPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
                  required
                  fullWidth
                  {...err('password')}
                />
                <PasswordField
                  id="user-password-confirm"
                  label={t('users.confirmPassword')}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  inputProps={{ maxLength: 30, autoComplete: 'new-password' }}
                  required
                  fullWidth
                  {...err('passwordConfirm')}
                />
              </>
            )}
          </FormSection>
        </Box>
        <Box data-tour="user-form-offices">
          <FormSection title={t('users.sectionOffices')} description={t('users.sectionOfficesHint')} columns={1}>
            <OfficeRoutesPicker
              offices={offices}
              routesByOffice={routesByOffice}
              value={officeRoutes}
              onChange={setOfficeRoutes}
              error={errors.offices}
            />
          </FormSection>
        </Box>
        <FormActions
          cancelTo="/admin/users"
          saving={saving}
          allowed={allowed}
          deniedTooltip={mode === 'new' ? t('common.noPermissionCreate') : t('common.noPermissionEdit')}
        />
      </Box>
    </AdminPage>
  );
};

export default UserFormPage;
